import { Pressable, View } from 'react-native';
import { style } from './PhotoCard.style';
import TextLato from '../text/text-lato/TextLato';
import { Icon } from '../icon/Icon';
import { ICON_NAMES } from '../icon/icon.helper';
import { brand, neutrals } from 'src/theme/colors';
import { PhotoCardProps } from './PhotoCard.types';
import { useStore } from 'src/contexts/StoreContext';
import Button from '../buttons/button/Button';
import { observer } from 'mobx-react-lite';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import Animated from 'react-native-reanimated';

export const PhotoCard = observer(
  forwardRef<View, PhotoCardProps>(
    ({ image, focusId, focusOrder, focusGroup, testID, onPress, onLongPress, closeZoom }, ref) => {
      const {
        i18n,
        dynamicModal: { openModal },
      } = useStore();
      const isSelected = image.isSelected;
      const counter = image.usageQuantity;
      const { isDesktop } = useResponsiveScreen();
      const [isHover, setHover] = useState(false);
      const { project } = useStore();

      const onHoverIn = useCallback(() => {
        setHover(true);
      }, []);

      const onHoverOut = useCallback(() => {
        setHover(false);
      }, []);

      const onPressDelete = useCallback(() => {
        openModal(
          {
            dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteFromDrawer,
            thumbnailsToDelete: [image.originalImageId],
          },
          true,
        );
      }, [project, image]);

      const children = useMemo(() => {
        return () => (
          <View style={[style.main, isSelected && style.border]}>
            <Animated.Image source={image.thumbnailUrl} style={[style.image]} pointerEvents="none" accessible={false} />
            {counter > 0 && (
              <View style={style.badge}>
                <TextLato style={style.badgeText}>{counter.toString()}</TextLato>
              </View>
            )}
            {isSelected && (
              <View style={style.check}>
                <Icon name={ICON_NAMES.Check} color={neutrals.white} />
              </View>
            )}
            {counter === 0 && (isHover || !isDesktop) && (
              <Pressable style={style.delete} onPress={onPressDelete} onHoverIn={onHoverIn}>
                <Icon name={ICON_NAMES.Delete} color={brand.main.water} />
              </Pressable>
            )}
          </View>
        );
      }, [counter, isSelected, isHover, image]);

      const imageRef = useRef<View>(null);

      useImperativeHandle(ref, () => imageRef?.current);

      return (
        <Button
          ref={imageRef}
          onFocus={e => e.stopPropagation()}
          onPress={async () => onPress()}
          accessibilityLabel={i18n.t('photo-drawer:select-photo')}
          accessible
          accessibilityRole="button"
          focusId={focusId}
          focusOrder={focusOrder}
          focusGroup={focusGroup}
          testID={testID}
          style={style.button}
          innerStyle={{ width: '100%', height: '100%' }}
          focusStyle={style.buttonFocus}
          onHoverIn={onHoverIn}
          onHoverOut={onHoverOut}
          onLongPress={() => onLongPress && onLongPress(image)}
          onPressOut={() => {
            closeZoom && closeZoom();
          }}
        >
          {children}
        </Button>
      );
    },
  ),
);
